import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn, EmptyState } from "@/lib/ui";
import { Bell, CheckCheck, AlertTriangle, ShieldCheck, UploadCloud, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

const TYPE_ICON = { approval: ShieldCheck, upload: UploadCloud, system: Bell, report: FileText, ai: AlertTriangle };
const PRIO_TONE = { high: "err", normal: "info", low: "muted" };

export default function Notifications() {
  const api = useApi();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications") });

  const readAll = useMutation({ mutationFn: () => api.post("/notifications/read-all"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["notifications"] }); qc.invalidateQueries({ queryKey: ["notif-count"] }); } });
  const readOne = useMutation({ mutationFn: (id) => api.post(`/notifications/${id}/read`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["notifications"] }); qc.invalidateQueries({ queryKey: ["notif-count"] }); } });

  if (isLoading) return <PageLoader />;
  const items = data?.items || [];

  return (
    <div>
      <PageHeader kicker="System" title="Notifications">
        <Btn variant="outline" size="sm" onClick={() => readAll.mutate()} data-testid="mark-all-read"><CheckCheck className="h-4 w-4" /> Mark all read</Btn>
      </PageHeader>
      {items.length === 0 ? (
        <EmptyState icon={Bell} title="No notifications" hint="AI alerts, approvals, integration failures, uploads and reports will surface here." />
      ) : (
        <Panel className="divide-y divide-border">
          {items.map((n) => {
            const Icon = TYPE_ICON[n.type] || Bell;
            return (
              <div key={n.id} className={cn("flex items-start gap-3 p-3", !n.read && "bg-primary/5")} data-testid={`notif-${n.id}`}>
                <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{n.title}</span>
                    <StatusBadge tone={PRIO_TONE[n.priority]}>{n.priority}</StatusBadge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                </div>
                {!n.read && <button onClick={() => readOne.mutate(n.id)} className="text-[11px] font-mono text-primary hover:underline" data-testid={`read-${n.id}`}>mark read</button>}
              </div>
            );
          })}
        </Panel>
      )}
    </div>
  );
}
